import React, { useState, useCallback, useEffect } from "react";
import {
  Box,
  Tooltip,
  Radio,
  RadioGroup,
  FormControlLabel,
  Typography,
  Button,
  CircularProgress,
} from "@mui/material";
import { PopupButton } from "react-calendly";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import useActiveUserDetail from "src/utils/shared/useActiveUserDetail";
import { useSnackbar } from "notistack";
import { useQuery } from "@apollo/client";
import GetProductFeatures from "src/graphql/query/productandpricing/getProductFeatures";
import clsx from "clsx";
import expressConfig from "src/config/express.json";
// import EnterpriseModal from "../RequestCallModal";
import { IPrices, IPlan, IFeatures } from "../dataTypes";
import useStyles from "./Styles";

interface Props {
  products: IPlan[];
  loader: boolean;
  handlePackagePurchase: any;
  handleClose: any;
  plan: string;
  // setPlan: (a: string) => void;
  setPlan: React.Dispatch<React.SetStateAction<string>>;
}

const PLAN_KEYS = ["basic", "standard", "business", "enterprise"] as const;
type PlanKey = (typeof PLAN_KEYS)[number];

const PricingData: React.FC<Props> = ({
  products,
  loader,
  handlePackagePurchase,
  handleClose,
  plan,
  setPlan,
}) => {
  const { classes } = useStyles();

  const env = process.env.NODE_ENV || "development";
  const config = (expressConfig as any)[env];

  const [rootElement, setRootElement] = useState<HTMLElement | null>(null);

  // ✅ Only set rootElement on client side (to avoid SSR issues)
  useEffect(() => {
    if (typeof document !== "undefined") {
      setRootElement(document.getElementById("root") || document.body);
    }
  }, []);

  const { enqueueSnackbar } = useSnackbar();
  const [productFeaturesList, setProductFeaturesList] = useState<IFeatures[]>(
    [],
  );

  const {
    // loading: getProductFeaturesQueryLoading,
    error: getProductFeaturesQueryError,
    data: getProductFeaturesQueryResult,
  } = useQuery(GetProductFeatures, { fetchPolicy: "network-only" });

  useEffect(() => {
    if (
      getProductFeaturesQueryResult &&
      getProductFeaturesQueryResult.getProductFeatures
    ) {
      try {
        const productFeatures =
          typeof JSON.parse(
            getProductFeaturesQueryResult.getProductFeatures,
          ) === "string"
            ? JSON.parse(
                JSON.parse(getProductFeaturesQueryResult.getProductFeatures),
              )
            : JSON.parse(getProductFeaturesQueryResult.getProductFeatures);
        setProductFeaturesList([...productFeatures]);
      } catch (err) {
        enqueueSnackbar("Error parsing product features data", {
          variant: "error",
        });
      }
    }
  }, [getProductFeaturesQueryResult]);

  useEffect(() => {
    if (getProductFeaturesQueryError) {
      enqueueSnackbar(getProductFeaturesQueryError.message, {
        variant: "error",
      });
    }
  }, [getProductFeaturesQueryError]);

  const { hasTrial } = useActiveUserDetail();

  // const [openModal, setOpenModal] = useState(false);

  // const handleOpenModal = () => {
  //   setOpenModal(true);
  // };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPlan(event.target.value);
  };

  const definedPlans = ["Basic", "Standard", "Business", "Enterprise"];

  const getPricesAccordingToPlan = (pricesList: IPrices[]) => {
    const currentPlan = plan === "annual" ? "year" : "month";
    if (currentPlan === "month") {
      const montlyPrice = pricesList.find((item) => item.interval === "month");

      if (montlyPrice) {
        return Math.ceil(montlyPrice.price / 100);
      }
    }

    const annualPrice = pricesList.find(
      (item) => item.interval === "year" && item.intervalCount === 1,
    );

    if (annualPrice) {
      return Math.ceil(annualPrice.price / 100 / 12);
    }
    return null;
  };

  const selectFeatureByPackage = useCallback(
    (
      bool: number | string,
      text: string | React.ReactNode,
      checkIndex: number,
    ) => {
      if (bool === 1)
        return (
          <Box key={checkIndex} className={classes.selectedFeatureBox}>
            <CheckCircleOutlineIcon className={classes.checkIcon} />
          </Box>
        );
      if (bool === 0)
        return (
          <Box key={checkIndex} className={classes.selectedFeatureBox}>
            <span className={classes.notCheckedIcon} />
          </Box>
        );
      return (
        <Box key={checkIndex} className={classes.textOfFeat}>
          {(() => {
            const parts = String(bool).split(" ");
            return (
              <>
                <span style={{ fontWeight: "600", marginRight: "5px" }}>
                  {parts[0] || ""}
                </span>
                <span>{parts[1] || ""}</span>
              </>
            );
          })()}
        </Box>
      );
    },
    [],
  );
  return (
    <Box
      className={classes.container}
      sx={{ maxHeight: "83vh", overflowY: "auto" }}
    >
      {/* <EnterpriseModal open={openModal} setOpen={setOpenModal} /> */}
      <Box>
        <Box className={classes.upperBox}>
          <Typography className={classes.upperBoxHeadText}>
            Choose the plan to suit your business
          </Typography>
          <Box className={classes.planHeaderContainer}>
            <RadioGroup value={plan} onChange={handleChange}>
              <FormControlLabel
                style={{ width: "fit-content" }}
                value="annual"
                control={<Radio color="primary" />}
                label={
                  <Typography
                    className={clsx(classes.intervalText, {
                      [classes.intervalTextChosen]: plan === "annual",
                    })}
                  >
                    Billed annually
                  </Typography>
                }
              />
              <FormControlLabel
                style={{ width: "fit-content" }}
                value="monthly"
                control={<Radio color="primary" />}
                label={
                  <Typography
                    className={clsx(classes.intervalText, {
                      [classes.intervalTextChosen]: plan === "monthly",
                    })}
                  >
                    Billed monthly
                  </Typography>
                }
              />
            </RadioGroup>
            {/* Other Columns */}

            {loader
              ? definedPlans.map((item) => (
                  <Box
                    className={classes.planNameContainer}
                    sx={{ position: "relative" }}
                  >
                    {item.toLowerCase() === "business" && (
                      <Typography className={classes.popularPlanText}>
                        MOST POPULAR
                      </Typography>
                    )}
                    <Typography className={classes.planNameText}>
                      {item}
                    </Typography>
                    <Typography className={classes.planNamePrice}>
                      {item.toLowerCase() === "enterprise" ? (
                        <span>Custom price</span>
                      ) : (
                        <CircularProgress
                          sx={{ color: "#4d1277", marginTop: "7px" }}
                          size={15}
                        />
                      )}
                    </Typography>
                    {item.toLowerCase() === "enterprise" ? (
                      rootElement && (
                        <PopupButton
                          url={config.CALENDLY_URL}
                          rootElement={rootElement}
                          text="Request a Call"
                          className={classes.planNameBtnEnterprise}
                        />
                        // <Button
                        //   className={classes.planNameBtnEnterprise}
                        //   style={{ marginTop: "10px" }}
                        // >
                        //   Request a call
                        // </Button>
                      )
                    ) : (
                      <Button
                        className={classes.planNameBtn}
                        style={{ marginTop: "10px" }}
                      >
                        Choose plan
                      </Button>
                    )}
                  </Box>
                ))
              : products.map((item) => (
                  <Box
                    className={classes.planNameContainer}
                    sx={{ position: "relative" }}
                  >
                    {item.name.toLowerCase() === "business" && (
                      <Typography className={classes.popularPlanText}>
                        MOST POPULAR
                      </Typography>
                    )}
                    <Typography className={classes.planNameText}>
                      {item.name}
                    </Typography>
                    <Typography className={classes.planNamePrice}>
                      {item.name.toLowerCase() === "enterprise" ? (
                        <span>Custom price</span>
                      ) : (
                        <>
                          <span style={{ fontWeight: "600" }}>
                            {" "}
                            {getPricesAccordingToPlan(item.prices)}
                          </span>{" "}
                          per agent / mo
                        </>
                      )}
                    </Typography>
                    {item.name.toLowerCase() === "enterprise" ? (
                      rootElement && (
                        <PopupButton
                          url={config.CALENDLY_URL}
                          rootElement={rootElement}
                          text="Request a Call"
                          className={classes.planNameBtnEnterprise}
                        />
                      )
                    ) : (
                      <Button
                        className={classes.planNameBtn}
                        disabled={
                          !!hasTrial && item.name.toLowerCase() === "free_trial"
                        }
                        onClick={() => {
                          handlePackagePurchase(
                            item.prices,
                            item.name,
                            item.productId,
                          );
                          handleClose();
                        }}
                      >
                        {!!hasTrial &&
                        item.name.toLowerCase() === "free_trial" ? (
                          <Typography
                            sx={{
                              color: "white",
                              textTransform: "capitalize",
                              margin: "0 -7px",
                            }}
                          >
                            Free_Trial is active
                          </Typography>
                        ) : (
                          "Choose plan"
                        )}
                      </Button>
                    )}
                  </Box>
                ))}
          </Box>
        </Box>
        {!productFeaturesList || productFeaturesList.length === 0 ? (
          <Box className={classes.loaderBox}>
            <CircularProgress className={classes.loaderStyle} />
          </Box>
        ) : (
          productFeaturesList.map((item: IFeatures, itemIndex: number) => (
            // eslint-disable-next-line react/no-array-index-key
            <Box key={itemIndex}>
              <Typography className={classes.categoryText}>
                {item.category}
              </Typography>
              {item.features.map((feature, featureIndex) => (
                <Box
                  // eslint-disable-next-line react/no-array-index-key
                  key={featureIndex}
                  className={classes.planFeatureRow}
                  sx={{
                    marginTop: featureIndex === 0 ? "20px" : 0,
                  }}
                >
                  <Box className={classes.featureTextBox}>
                    <Typography className={classes.featureText}>
                      {feature.name}
                    </Typography>
                    <Tooltip
                      title={feature.description}
                      arrow
                      placement="right"
                    >
                      <ErrorOutlineIcon className={classes.tooltip} />
                    </Tooltip>
                  </Box>
                  {/* for ordering in the specific order */}
                  {PLAN_KEYS.map((planName: PlanKey, index) => {
                    const value = feature.allowed?.[planName];
                    return selectFeatureByPackage(value, planName, index);
                  })}
                </Box>
              ))}
            </Box>
          ))
        )}
      </Box>
    </Box>
  );
};

export default PricingData;
